/**
 * Image optimization utility for Supabase Storage
 * Uses Supabase's built-in image transformation API to serve optimized images
 */

export const IMAGE_SIZES = {
  thumbnail: { width: 100, height: 100, quality: 70 },
  small: { width: 200, height: 200, quality: 75 },
  medium: { width: 400, height: 400, quality: 80 },
  large: { width: 800, height: 800, quality: 85 },
  card: { width: 300, height: 400, quality: 80 },
  avatar: { width: 150, height: 150, quality: 80 },
  profileCard: { width: 256, height: 288, quality: 80 },
  miniAvatar: { width: 64, height: 80, quality: 75 },
  albumCard: { width: 300, height: 300, quality: 80 },
  eventCard: { width: 400, height: 533, quality: 80 },
  eventHero: { width: 800, height: 1066, quality: 85 },
  eventThumb: { width: 80, height: 80, quality: 75 },
} as const;

export type ImageSizePreset = keyof typeof IMAGE_SIZES;

interface CustomImageSize {
  width: number;
  height: number;
  quality?: number;
}

/**
 * Check if the URL is from Supabase storage and can be transformed
 */
function isSupabaseStorageUrl(url: string): boolean {
  return url.includes(".supabase.co/storage/v1/object/");
}

/**
 * Convert a public URL to a render URL for transformations
 * Supabase transforms require /render/image/ instead of /object/
 */
function convertToRenderUrl(url: string): string {
  return url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
}

/**
 * Get an optimized image URL with Supabase transformations
 *
 * @param originalUrl - The original image URL
 * @param size - Either a preset name or custom dimensions
 * @returns Optimized URL with transform parameters (for Supabase) or original URL
 */
export function getOptimizedImageUrl(
  originalUrl: string | undefined | null,
  size: ImageSizePreset | CustomImageSize = "medium",
): string {
  if (!originalUrl) return "";

  // Get size configuration
  const sizeConfig = typeof size === "string" ? IMAGE_SIZES[size] : size;
  const { width, height, quality = 80 } = sizeConfig;

  // Only transform Supabase storage URLs
  if (!isSupabaseStorageUrl(originalUrl)) {
    return originalUrl;
  }

  // Remove any existing query parameters
  const baseUrl = originalUrl.split("?")[0];

  // Convert to render URL for transformations
  const renderUrl = convertToRenderUrl(baseUrl);

  // Build transform parameters
  const params = new URLSearchParams({
    width: width.toString(),
    height: height.toString(),
    quality: quality.toString(),
    resize: "cover",
  });

  return `${renderUrl}?${params.toString()}`;
}

/**
 * Generate srcset for responsive images
 *
 * @param originalUrl - The original image URL
 * @param widths - Array of widths to generate
 * @param aspectRatio - Height/Width ratio (default 1:1)
 * @returns srcset string for use in img elements
 */
export function generateSrcSet(
  originalUrl: string | undefined | null,
  widths: number[] = [200, 400, 800],
  aspectRatio: number = 1,
): string {
  if (!originalUrl) return "";

  return widths
    .map((width) => {
      const height = Math.round(width * aspectRatio);
      const url = getOptimizedImageUrl(originalUrl, {
        width,
        height,
        quality: 80,
      });
      return `${url} ${width}w`;
    })
    .join(", ");
}
